import { Platform } from 'react-native'
import { Href, router } from 'expo-router'
import { JsonMap } from '@segment/analytics-react-native'

import { useInitialLinkingStore } from '../../hooks/useInitialLinkingStore'
import { useSessionStore } from '../session/useSessionStore'
import { resolveDeepLink } from './deeplink-resolver'

export * from './deeplink-resolver'

/** Matches `analytics.setEvent` so the store action can be passed straight in. */
type TrackEvent = (event: string, payload?: JsonMap) => void

/**
 * Single auth-gated entry point for every deep link, whatever delivered it —
 * a OneSignal notification click, a Customer.io push opened via Linking, a
 * universal/Branch link, or the cold-start launch URL.
 *
 * Unauthenticated: stash the canonical URL so the post-login screens
 * (sign-up, StartPlaying, UserInfo) can replay it. Authenticated: navigate now.
 */
export function handleDeepLink(params: { raw?: string | null; source: string; track: TrackEvent }) {
  const { raw, source, track } = params
  const resolution = resolveDeepLink(raw)

  if (!resolution.ok) {
    // `empty` just means this notification carried no link — not worth an event.
    if (resolution.reason !== 'empty') {
      track('Deep Link Skipped', { url: raw ?? '', reason: resolution.reason, source })
    }
    return
  }

  const { href, url } = resolution.target
  const isAuthenticated = Boolean(useSessionStore.getState().getAccessToken())

  if (!isAuthenticated) {
    track('Deep Link Captured', { url: href, source })
    useInitialLinkingStore.getState().add(url)
    return
  }

  // Web routes deep links through the browser URL bar, not the router.
  if (Platform.OS === 'web') return

  track('Deep Link Consumed', { url: href, source })
  router.navigate(href as Href)
}
